import { Divider, Typography } from '@mui/material';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useContext, useEffect } from 'react';
import { styled } from 'styled-components';
import { Order, Product } from '../ChatMessage';
import { useNavigate, useParams } from 'react-router-dom';
import { canEditOrder, getBaseRoute, getStatusStyles, truncateField } from '@src/helpers/Helpers';
import moment from 'moment';
import { RESTAURANT } from '@src/Constants';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomTooltip from '@src/components/CustomTootip/CustomTooltip';
import CustomPermission from '@src/components/Permissions/CustomPermission';
import { MarketplaceIcon, MarketplaceShopIcon } from '@src/components/Icons';

const StyledContainer = styled.div<{ type: string }>`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const OrderHeader = styled.div<{ orderBy: string }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	width: 100%;
	.orderInfo {
		display: flex;
		flex-direction: column;
		gap: 2px;
		height: ${(props) => (props.orderBy === '' ? '40px' : '55px')};
		font-family: Nunito Sans;
		font-weight: 600;

		h3 {
			color: #000;
			font-size: 14px;
			line-height: 19.1px;
		}

		#orderPlace {
			display: flex;
			gap: 4px;
			color: var(--lighter-text-color);
			font-size: 10px;
			line-height: 13px;
		}
	}
`;

const OrderInfo = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: 5px;
	width: 100%;
	height: 140px;
	padding: 10px;
	font-family: Nunito Sans;
	font-weight: 600;
	div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 30px;
		width: 100%;
		p {
			color: var(--lighter-text-color);
			font-size: 10px;
			line-height: 13px;
		}
		span {
			font-size: 10px;
			color: #000;
			line-height: 13px;
		}
		.status {
			display: flex;
			align-items: center;
			justify-content: center;
			height: 23px;
			border-radius: 4px;
			font-size: 14px;
			font-weight: 700;
			line-height: 19.1px;
			padding: 2px 6px;
		}
	}
`;

const OrderDetailsBtn = styled.div<{ orderBy: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 45px;
	div {
		color: #e6ab00;
		font-size: 14px;
		font-weight: 700;
		cursor: pointer;
	}
`;

const StyledUserName = styled.div<{ orderBy: string }>`
	margin-bottom: ${(props) => (props?.orderBy === '' ? '0' : '4px')};

	p {
		color: var(--color-font-grey);
		font-size: 10px;
		font-weight: 600;
		line-height: 13px;
		overflow: hidden;
		text-overflow: ellipsis;
		width: 200px;
	}
`;

const StyledTextButton = styled.button`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 18.2px;
	letter-spacing: -0.28px;
	text-decoration-line: underline;
	cursor: pointer;
	background: none;
	border: none;
	padding: 0;
`;

const StyledUpdateLabel = styled.div<{ data: any }>`
	width: 100%;
	display: flex;
	align-items: center;
	padding-left: 10px;
	border-radius: 10px 10px 0px 0px;
	background: #ffe599;
	height: 28px;
	color: var(--default-text-color);
	font-size: 10px;
	font-weight: 600;
	line-height: 13px;
`;

const StyledDeliveryRequestLabel = styled.div<{ data: any }>`
	width: 100%;
	display: flex;
	align-items: center;
	border-radius: 10px 10px 0px 0px;
	height: 28px;
	color: var(--default-text-color);
	font-size: 14px;
	font-weight: 700;
	line-height: 13px;
	padding: 10px;
	letter-spacing: 0.28px;
`;

interface OrderMessageProps {
	orderDetails: Order;
	orderInfo: any;
	orderBy: string;
	setLoading: (data: boolean) => void;
	currentUser: any;
	setViewOrder: (data: boolean) => void;
	setViewAprovalOrder: (data: boolean) => void;
	data: any;
	chatTimeStamp: any;
	widgetType: 'create' | 'update';
	setFieldBackField: any;
	setOrderDetails: any;
	orderData: Order;
}

const OrderMessage: React.FC<OrderMessageProps> = ({
	currentUser,
	setViewOrder,
	setViewAprovalOrder,
	orderInfo,
	orderBy,
	data,
	orderDetails,
	chatTimeStamp,
	widgetType = 'create',
	setFieldBackField,
	setOrderDetails,
	orderData,
}) => {
	const navigate = useNavigate();
	const { id, waId } = useParams();

	//Permission
	const checkPermission = usePermission();
	const editOrderPermission = checkPermission(ALL_PERMISSION_MAP?.GENERAL?.ORDER?.UPDATE, waId);
	const sendMessagePermission = checkPermission(ALL_PERMISSION_MAP?.GENERAL?.MESSAGE?.SEND, waId);

	const canViewOrder = checkPermission(
		ALL_PERMISSION_MAP?.GENERAL?.ORDER?.VIEW,
		orderDetails?.timeId || orderDetails?.orderId
	);

	const openOrderView = () => {
		setOrderDetails(orderData);
		setViewOrder(true);
	};

	const handleNavigation = (path: string) => {
		navigate(path);
	};

	const updateMessageField = () => {
		setFieldBackField(orderDetails);
	};

	const orderHeader = () => {
		const orderDate = orderDetails?.orderDate || orderData?.orderDate;

		return (
			<OrderHeader orderBy={orderBy}>
				<div className="orderInfo">
					{/* <StyledUserName orderBy={orderBy}>
						<p>{orderBy}</p>
					</StyledUserName> */}
					<h3>{`${orderDetails?.timeId || orderData?.timeId ? '' : 'Order#'} ${
						orderDetails?.timeId || orderData?.timeId || orderDetails?.orderId || orderData?.orderId
					}`}</h3>
					<div id="orderPlace">
						<p>{orderDate}</p>
						{/* {chatTimeStamp} */}
					</div>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-end',
						gap: '6px',
					}}
				>
					{orderDetails?.isMarketplace ? <MarketplaceShopIcon /> : null}
					{orderDetails?.orderStatus?.validation_order &&
					canEditOrder(orderDetails?.orderStatus?.validation_order) ? (
						editOrderPermission ? (
							<StyledTextButton
								onClick={() =>
									handleNavigation(
										`/${getBaseRoute(currentUser?.type)}/${id}/${waId}/editOrder/${
											orderDetails?.orderId
										}`
									)
								}
							>
								Edit Order
							</StyledTextButton>
						) : (
							<CustomTooltip
								title="You do not have permission to view chats. Contact 
				an administrator to get permission."
								placement="bottom"
							>
								<span>
									<StyledTextButton onClick={() => {}} disabled>
										Edit Order
									</StyledTextButton>
								</span>
							</CustomTooltip>
						)
					) : null}
				</div>
			</OrderHeader>
		);
	};

	const deliveryRequestTitle = (orderDetails: Order) => {
		const isAccepted = orderDetails?.products?.every(
			(product: Product) => product?.delivery_acc_status?.toLowerCase() === 'accepted'
		);
		const isRejected = orderDetails?.products?.every(
			(product: Product) => product?.delivery_acc_status?.toLowerCase() === 'rejected'
		);
		if (isAccepted) {
			return 'Accepted';
		} else if (isRejected) {
			return 'Rejected';
		}
		return 'See Details';
	};

	const bgColors: any = {
		accepted: '#00C853',
		rejected: '#F26184',
	};

	return (
		<>
			{canViewOrder ? (
				<StyledContainer type={currentUser?.type}>
					{data?.payload?.order?.isUpdated ? (
						<StyledUpdateLabel data={data}>Order Updated</StyledUpdateLabel>
					) : null}
					{data?.payload?.order?.isDeliveryCheckRequest ? (
						<StyledDeliveryRequestLabel data={data}>
							<StyledTextButton
								style={{
									color:
										bgColors?.[deliveryRequestTitle(orderDetails)?.toLowerCase()] ||
										'rgb(94, 148, 255)',
									paddingTop: '6px',
								}}
								onClick={() => {
									setOrderDetails(orderData);
									setViewAprovalOrder(true);
								}}
							>
								{deliveryRequestTitle(orderDetails)}
							</StyledTextButton>
						</StyledDeliveryRequestLabel>
					) : null}
					{orderHeader()}
					<Divider style={{ width: '100%' }} />
					<OrderInfo>
						{orderInfo?.map((info: any) => {
							if (info?.id === 'status') {
								const { name, color, color_bg } = data?.payload?.order?.orderStatus || {};
								const textColor = color || info?.value?.color;
								const bgColor = color_bg || info?.value?.color_bg;
								const statusName = name || info?.value?.name;

								return (
									<div>
										<p>{info?.key}</p>

										<span
											style={{
												color: textColor,
												background: bgColor,
											}}
											className={info?.className || ''}
										>
											{truncateField(statusName, 20)}
										</span>
									</div>
								);
							}

							return (
								<div>
									<p>{info?.key}</p>

									<span className={info?.className || ''}>{truncateField(info?.value, 20)}</span>
								</div>
							);
						})}
					</OrderInfo>
					<Divider style={{ width: '100%' }} />
					<OrderDetailsBtn orderBy={orderBy}>
						<div onClick={openOrderView}>View Details</div>
					</OrderDetailsBtn>
				</StyledContainer>
			) : (
				<CustomPermission
					containerStyle={{ height: '239px' }}
					headingStyle={{ fontSize: '28px' }}
					paraStyle={{ fontSize: '14px', width: '200px' }}
					title="Permission Denied"
					paragraph="You can't view orders. Contact an administrator to get permission"
				/>
			)}
		</>
	);
};

export default OrderMessage;
